// src\components\CustomerSummary\components\StudiesStartedTable.jsx

/* ============================================================================
   BLOCK CS.UI.T1 — StudiesStartedTable (editable base rows + live derived)
   ----------------------------------------------------------------------------
   BEHAVIOR
   - Base category rows are editable (counts)
   - Totals / Monthly averages / Grand totals are derived (read-only)
   - Edits are stored locally, then written back into the section text via
     onChangeText(buildText(...))
   - When locked:
       • editable base rows render as plain text (NOT disabled inputs)
       • derived rows remain plain text
   - When backend regenerates:
       • text prop changes → component re-parses and resets edit state
============================================================================ */

import React from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// ============================================================================
// BLOCK CS.UI.T1.1 — Parsing helpers (header + table rows)
// ============================================================================
function isHeaderLine(line) {
  const s = String(line || '').trim();
  return s.startsWith('Facility:') || s.startsWith('Date Range:');
}

function parseDateRange(headerLines) {
  const line = (headerLines || []).find((l) => String(l).startsWith('Date Range:'));
  if (!line) {
    return { start: '', end: '' };
  }

  // Expected: "Date Range: YYYY-MM-DD -> YYYY-MM-DD"
  const m = String(line).match(
    /Date Range:\s*([0-9]{4}-[0-9]{2}-[0-9]{2})\s*->\s*([0-9]{4}-[0-9]{2}-[0-9]{2})/
  );
  if (!m) {
    return { start: '', end: '' };
  }

  return { start: m[1], end: m[2] };
}

// Inclusive months count (matches backend intent)
function monthsInclusive(startYmd, endYmd) {
  const s = String(startYmd || '');
  const e = String(endYmd || '');
  const sm = s.match(/^(\d{4})-(\d{2})-/);
  const em = e.match(/^(\d{4})-(\d{2})-/);
  if (!sm || !em) {
    return 1;
  }

  const sy = Number(sm[1]);
  const sMon = Number(sm[2]);
  const ey = Number(em[1]);
  const eMon = Number(em[2]);

  if (!sy || !sMon || !ey || !eMon) {
    return 1;
  }

  const a = sy * 12 + (sMon - 1);
  const b = ey * 12 + (eMon - 1);
  return Math.max(1, b - a + 1);
}

function parseStudiesStarted(text) {
  const raw = String(text || '');
  const lines = raw.split(/\r?\n/);

  const headerLines = lines.filter((l) => isHeaderLine(l)).map((l) => l.trim());

  const idx = lines.findIndex((l) =>
    String(l || '')
      .toLowerCase()
      .includes('studies started')
  );

  const dataLines = idx >= 0 ? lines.slice(idx + 1) : lines;

  const rows = dataLines
    .map((l) => String(l || '').trim())
    .filter((l) => l.length > 0)
    .map((l) => l.split('\t').map((x) => String(x || '').trim()))
    .filter((parts) => parts.length >= 2)
    .map((parts) => ({
      label: parts[0] || '',
      workflow: parts[1] || '',
      value: parts[2] || '',
    }));

  return { headerLines, rows };
}

// ============================================================================
// BLOCK CS.UI.T1.2 — Row classification + formatting helpers
// ============================================================================
function rowStyle(label) {
  const l = String(label || '').toLowerCase();
  const isGrand =
    l === 'grand total' || l === 'combined monthly average' || l.includes('grand total');
  const isSubtotal = l === 'total' || l === 'monthly average';
  return { isGrand, isSubtotal };
}

function isEditableBaseRow(label) {
  const s = String(label || '').trim();
  return (
    s === 'Holter' ||
    s === 'Extended Holter' ||
    s === 'Cardiac Event Monitoring (CEM)' ||
    s === 'Mobile Cardiac Telemetry (MCT)'
  );
}

function normWorkflow(w) {
  const s = String(w || '')
    .trim()
    .toLowerCase();
  if (s.includes('ship')) {
    return 'ship';
  }
  return 'clinic';
}

function toInt(v) {
  const n = Number(String(v ?? '').replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n)) {
    return 0;
  }
  return Math.max(0, Math.trunc(n));
}

function fmtInt(n) {
  return String(toInt(n));
}

function fmtFixed2(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) {
    return '0.00';
  }
  return x.toFixed(2);
}

function buildText({ headerLines, rows }) {
  const out = [];
  headerLines.forEach((h) => out.push(h));
  out.push('');
  out.push('Studies Started\t\t');

  rows.forEach((r) => {
    out.push(`${r.label}\t${r.workflow}\t${r.value}`);
  });

  return out.join('\n');
}

// ============================================================================
// BLOCK CS.UI.T1.3 — Component
// ============================================================================
const DEFAULT_WIDTHS = {
  col1: 360,
  col2: 280,
  col3: 160,
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function parseWidths(input) {
  const col1 = Number(input?.col1);
  const col2 = Number(input?.col2);
  const col3 = Number(input?.col3);

  const ok =
    Number.isFinite(col1) &&
    Number.isFinite(col2) &&
    Number.isFinite(col3) &&
    col1 > 0 &&
    col2 > 0 &&
    col3 > 0;

  return ok ? { col1, col2, col3 } : null;
}

function ColumnDivider({ onMouseDown }) {
  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        position: 'absolute',
        top: 0,
        right: -6,
        width: 12,
        height: '100%',
        cursor: 'col-resize',
        zIndex: 5,
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '1px',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.18)',
        },
        '&:hover::after': {
          backgroundColor: 'rgba(0,0,0,0.35)',
        },
      }}
    />
  );
}

function useColumnResize({ widths, setWidths, onPersistWidths }) {
  const dragRef = React.useRef(null);

  const startDrag = React.useCallback(
    (which, e) => {
      e.preventDefault();
      e.stopPropagation();

      dragRef.current = { which, startX: e.clientX, start: { ...widths } };

      const onMove = (ev) => {
        const drag = dragRef.current;
        if (!drag) {
          return;
        }

        const dx = ev.clientX - drag.startX;
        const minCol = 140;
        const maxCol = 1200;

        if (drag.which === 'c1') {
          const total = drag.start.col1 + drag.start.col2;
          const nextCol1 = clamp(drag.start.col1 + dx, minCol, maxCol);
          const nextCol2 = clamp(total - nextCol1, minCol, maxCol);

          const fixedTotal = nextCol1 + nextCol2;
          const finalCol1 = clamp(nextCol1 + (total - fixedTotal), minCol, maxCol);

          setWidths((p) => ({ ...p, col1: finalCol1, col2: total - finalCol1 }));
        }

        if (drag.which === 'c2') {
          const total = drag.start.col2 + drag.start.col3;
          const nextCol2 = clamp(drag.start.col2 + dx, minCol, maxCol);
          const nextCol3 = clamp(total - nextCol2, minCol, maxCol);

          const fixedTotal = nextCol2 + nextCol3;
          const finalCol2 = clamp(nextCol2 + (total - fixedTotal), minCol, maxCol);

          setWidths((p) => ({ ...p, col2: finalCol2, col3: total - finalCol2 }));
        }

        if (drag.which === 'c3') {
          const nextCol3 = clamp(drag.start.col3 + dx, minCol, maxCol);
          setWidths((p) => ({ ...p, col3: nextCol3 }));
        }
      };

      const onUp = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        onPersistWidths();
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [onPersistWidths, setWidths, widths]
  );

  return { startDrag };
}

export default function StudiesStartedTable({
  text,
  locked = false,
  onChangeText = () => {},
  columnWidths,
  onColumnWidthsChange,
}) {
  // Parsed source text (header + any prior rows)
  const parsed = React.useMemo(() => parseStudiesStarted(text), [text]);
  const { headerLines } = parsed;

  const initialWidths = React.useMemo(() => {
    const fromProp = parseWidths(columnWidths);
    if (fromProp) {
      return fromProp;
    }

    return { ...DEFAULT_WIDTHS };
  }, [columnWidths]);

  const [widths, setWidths] = React.useState(initialWidths);
  const widthsRef = React.useRef(widths);

  React.useEffect(() => {
    widthsRef.current = widths;
  }, [widths]);

  React.useEffect(() => {
    const fromProp = parseWidths(columnWidths);
    if (!fromProp) {
      return;
    }

    setWidths((prev) => {
      const same =
        Number(prev.col1) === Number(fromProp.col1) &&
        Number(prev.col2) === Number(fromProp.col2) &&
        Number(prev.col3) === Number(fromProp.col3);
      return same ? prev : fromProp;
    });
  }, [columnWidths]);

  const persistWidths = React.useCallback(() => {
    if (typeof onColumnWidthsChange === 'function') {
      onColumnWidthsChange({ ...widthsRef.current });
    }
  }, [onColumnWidthsChange]);

  const { startDrag } = useColumnResize({ widths, setWidths, onPersistWidths: persistWidths });

  const onDividerC1MouseDown = React.useCallback((e) => startDrag('c1', e), [startDrag]);
  const onDividerC2MouseDown = React.useCallback((e) => startDrag('c2', e), [startDrag]);
  const onDividerC3MouseDown = React.useCallback((e) => startDrag('c3', e), [startDrag]);

  const totalWidth = widths.col1 + widths.col2 + widths.col3;

  // ============================================================================
  // BLOCK CS.UI.T1.4 — Local edit state (resets on upstream text change)
  // ----------------------------------------------------------------------------
  // We store edits in a map keyed by `${label}||${workflow}`.
  // Any upstream text change (regen/global) clears edits to reflect source-of-truth.
  // ============================================================================
  const [edits, setEdits] = React.useState({});

  React.useEffect(() => {
    setEdits({});
  }, [text]);

  // Date range → months
  const { start, end } = React.useMemo(() => parseDateRange(headerLines), [headerLines]);
  const months = React.useMemo(() => monthsInclusive(start, end), [start, end]);

  // ============================================================================
  // BLOCK CS.UI.T1.5 — Base values (parsed rows + edits overlay)
  // ----------------------------------------------------------------------------
  // Normalize to a known shape for clinic/ship workflows and the 4 editable categories.
  // ============================================================================
  const base = React.useMemo(() => {
    const m = {
      clinic: {
        Holter: 0,
        'Extended Holter': 0,
        'Cardiac Event Monitoring (CEM)': 0,
        'Mobile Cardiac Telemetry (MCT)': 0,
      },
      ship: {
        Holter: 0,
        'Extended Holter': 0,
        'Cardiac Event Monitoring (CEM)': 0,
        'Mobile Cardiac Telemetry (MCT)': 0,
      },
    };

    parsed.rows.forEach((r) => {
      if (!isEditableBaseRow(r.label)) {
        return;
      }

      const wf = normWorkflow(r.workflow);
      const key = `${r.label}||${r.workflow}`;
      const v = Object.prototype.hasOwnProperty.call(edits, key) ? edits[key] : r.value;

      m[wf][r.label] = toInt(v);
    });

    return m;
  }, [parsed.rows, edits]);

  // ============================================================================
  // BLOCK CS.UI.T1.6 — Derived rows (totals, monthly averages, grand totals)
  // ============================================================================
  const derivedRows = React.useMemo(() => {
    const categories = [
      'Holter',
      'Extended Holter',
      'Cardiac Event Monitoring (CEM)',
      'Mobile Cardiac Telemetry (MCT)',
    ];

    const workflowLabels = {
      clinic: 'In-clinic connection',
      ship: 'Ship-to-patient',
    };

    const rows = [];

    const totalFor = (wf) => categories.reduce((acc, c) => acc + toInt(base[wf][c]), 0);

    const clinicTotal = totalFor('clinic');
    const shipTotal = totalFor('ship');
    const grandTotal = clinicTotal + shipTotal;

    const clinicMonthly = clinicTotal / months;
    const shipMonthly = shipTotal / months;
    const combinedMonthly = grandTotal / months;

    // Clinic (editable base rows)
    categories.forEach((c) => {
      rows.push({
        label: c,
        workflow: workflowLabels.clinic,
        value: fmtInt(base.clinic[c]),
        editable: true,
      });
    });
    rows.push({
      label: 'Total',
      workflow: workflowLabels.clinic,
      value: fmtInt(clinicTotal),
      editable: false,
    });
    rows.push({
      label: 'Monthly Average',
      workflow: workflowLabels.clinic,
      value: fmtFixed2(clinicMonthly),
      editable: false,
    });

    // Ship-to-patient (editable base rows)
    categories.forEach((c) => {
      rows.push({
        label: c,
        workflow: workflowLabels.ship,
        value: fmtInt(base.ship[c]),
        editable: true,
      });
    });
    rows.push({
      label: 'Total',
      workflow: workflowLabels.ship,
      value: fmtInt(shipTotal),
      editable: false,
    });
    rows.push({
      label: 'Monthly Average',
      workflow: workflowLabels.ship,
      value: fmtFixed2(shipMonthly),
      editable: false,
    });

    // Grand totals
    rows.push({ label: 'Grand Total', workflow: '', value: fmtInt(grandTotal), editable: false });
    rows.push({
      label: 'Combined Monthly Average',
      workflow: '',
      value: fmtFixed2(combinedMonthly),
      editable: false,
    });

    return rows;
  }, [base, months]);

  // ============================================================================
  // BLOCK CS.UI.T1.7 — Push edits upward as regenerated section text
  // ----------------------------------------------------------------------------
  // Note: derivedRows is the canonical table representation; we rebuild the
  // section text from it and pass it upward.
  // ============================================================================
  React.useEffect(() => {
    if (typeof onChangeText !== 'function') {
      return;
    }

    const nextText = buildText({ headerLines, rows: derivedRows });
    onChangeText(nextText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edits, headerLines, derivedRows]);

  // ============================================================================
  // BLOCK CS.UI.T1.8 — Edit handler
  // ============================================================================
  function handleEdit(label, workflow, nextVal) {
    const k = `${label}||${workflow}`;
    setEdits((p) => ({ ...p, [k]: nextVal }));
  }

  // ============================================================================
  // BLOCK CS.UI.T2 — Count cell renderer (locked mode = plain text)
  // ----------------------------------------------------------------------------
  // UX:
  // - Unlocked + editable row => TextField input
  // - Locked OR derived row   => plain text (matches derived row styling)
  // ============================================================================
  function renderCountCell(r) {
    if (r.editable && !locked) {
      return (
        <TextField
          type="number"
          size="small"
          value={r.value}
          onChange={(e) => handleEdit(r.label, r.workflow, e.target.value)}
          inputProps={{ min: 0 }}
          sx={{ width: 90 }}
        />
      );
    }

    return r.value;
  }

  // ============================================================================
  // BLOCK CS.UI.T3 — Render
  // ============================================================================
  return (
    <Box>
      {headerLines.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {headerLines.map((l) => (
            <Typography key={l} variant="body2" sx={{ lineHeight: 1.2 }}>
              {l}
            </Typography>
          ))}
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ tableLayout: 'fixed', width: totalWidth, minWidth: totalWidth }}>
          <colgroup>
            <col style={{ width: `${widths.col1}px` }} />
            <col style={{ width: `${widths.col2}px` }} />
            <col style={{ width: `${widths.col3}px` }} />
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, width: `${widths.col1}px`, position: 'relative' }}>
                Study Type
                <ColumnDivider onMouseDown={onDividerC1MouseDown} />
              </TableCell>
              <TableCell sx={{ fontWeight: 800, width: `${widths.col2}px`, position: 'relative' }}>
                Workflow
                <ColumnDivider onMouseDown={onDividerC2MouseDown} />
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 800,
                  width: `${widths.col3}px`,
                  position: 'relative',
                }}
              >
                Count
                <ColumnDivider onMouseDown={onDividerC3MouseDown} />
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {derivedRows.map((r) => {
              const { isGrand, isSubtotal } = rowStyle(r.label);

              return (
                <TableRow
                  key={`${r.label}-${r.workflow}`}
                  sx={{
                    backgroundColor: isGrand ? 'rgba(255, 235, 59, 0.20)' : 'transparent',
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: isGrand || isSubtotal ? 800 : 400,
                      borderTop: isSubtotal ? '1px solid rgba(0,0,0,0.35)' : undefined,
                    }}
                  >
                    {r.label}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontWeight: isGrand || isSubtotal ? 800 : 400,
                      borderTop: isSubtotal ? '1px solid rgba(0,0,0,0.35)' : undefined,
                    }}
                  >
                    {r.workflow}
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: isGrand || isSubtotal ? 800 : 400,
                      borderTop: isSubtotal ? '1px solid rgba(0,0,0,0.35)' : undefined,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {renderCountCell(r)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
